'use client';

import { useEffect, useMemo, useState } from 'react';
import { Copy, Download, RotateCcw, FileText, Check } from 'lucide-react';

type Status = 'Draft' | 'In Review' | 'Approved' | 'Shipped';

type Form = {
  featureName: string;
  author: string;
  status: Status;
  summary: string;
  problem: string;
  targetUsers: string;
  goals: string;
  nonGoals: string;
  solution: string;
  userStories: string;
  metrics: string;
  openQuestions: string;
  risks: string;
};

const STORAGE_KEY = 'planetroadmap.prd-generator.v1';

const EXAMPLE: Form = {
  featureName: 'Inline comments on roadmap items',
  author: 'Sean Conroy',
  status: 'Draft',
  summary:
    'Let teammates leave threaded comments directly on a roadmap item so context lives next to the work, not in Slack.',
  problem:
    'Discussion about roadmap items happens in chat, where it is lost the next day. Reviewers and stakeholders cannot see why a decision was made, and PMs spend time re-explaining tradeoffs that were resolved weeks ago.',
  targetUsers:
    'Product managers and the cross-functional reviewers (engineering leads, design, support) who weigh in on roadmap items before they ship.',
  goals: `Cut "what was decided here?" questions in half within 30 days
Allow @-mentions and email notifications
Render Markdown in comments`,
  nonGoals: `Real-time multiplayer cursors
Voice or video comments
Custom emoji reactions (defer to v2)`,
  solution:
    'Add a comments panel beside each roadmap item. Comments support Markdown, @-mentions, and threaded replies. Notifications fire by email and in the in-app inbox. Comments are scoped to the workspace and respect the item\'s visibility (internal vs. public).',
  userStories: `As a PM, I want to leave a comment on a roadmap item so the rationale is preserved
As a reviewer, I want to be notified when I am @-mentioned so I do not miss requests
As a stakeholder, I want to read past discussion so I can catch up without asking`,
  metrics: `% of roadmap items with at least one comment within 7 days of creation
Average comments per item per week
Notification click-through rate`,
  openQuestions: `Should public roadmap items show internal comments at all?
Do we send a digest if a user has many unread mentions?`,
  risks: `Email volume could overwhelm low-engagement users — solve with a preferences page
Markdown rendering needs sanitization to prevent XSS`,
};

const BLANK: Form = {
  featureName: '',
  author: '',
  status: 'Draft',
  summary: '',
  problem: '',
  targetUsers: '',
  goals: '',
  nonGoals: '',
  solution: '',
  userStories: '',
  metrics: '',
  openQuestions: '',
  risks: '',
};

function bulletize(raw: string): string {
  return raw
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => `- ${line.replace(/^[-*]\s*/, '')}`)
    .join('\n');
}

function paragraph(raw: string): string {
  return raw.trim();
}

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function buildMarkdown(form: Form): string {
  const sections: string[] = [];
  const title = form.featureName.trim() || 'Untitled Feature';
  sections.push(`# ${title}`);

  const meta: string[] = [];
  if (form.author.trim()) meta.push(`**Author:** ${form.author.trim()}`);
  meta.push(`**Status:** ${form.status}`);
  meta.push(`**Last updated:** ${todayIso()}`);
  sections.push(meta.join('  \n'));

  if (form.summary.trim()) sections.push(`## Summary\n${paragraph(form.summary)}`);
  if (form.problem.trim()) sections.push(`## Problem\n${paragraph(form.problem)}`);
  if (form.targetUsers.trim()) sections.push(`## Target users\n${paragraph(form.targetUsers)}`);
  if (form.goals.trim()) sections.push(`## Goals\n${bulletize(form.goals)}`);
  if (form.nonGoals.trim()) sections.push(`## Non-goals\n${bulletize(form.nonGoals)}`);
  if (form.solution.trim()) sections.push(`## Proposed solution\n${paragraph(form.solution)}`);
  if (form.userStories.trim()) sections.push(`## User stories\n${bulletize(form.userStories)}`);
  if (form.metrics.trim()) sections.push(`## Success metrics\n${bulletize(form.metrics)}`);
  if (form.openQuestions.trim())
    sections.push(`## Open questions\n${bulletize(form.openQuestions)}`);
  if (form.risks.trim()) sections.push(`## Risks & dependencies\n${bulletize(form.risks)}`);

  return sections.join('\n\n');
}

export function PrdGenerator() {
  const [form, setForm] = useState<Form>(BLANK);
  const [hydrated, setHydrated] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Form;
        if (parsed && typeof parsed === 'object') {
          setForm({ ...BLANK, ...parsed });
          setHydrated(true);
          return;
        }
      }
    } catch {}
    setForm(EXAMPLE);
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(form));
    } catch (err) {
      console.warn('Failed to persist to localStorage:', err);
    }
  }, [form, hydrated]);

  const markdown = useMemo(() => buildMarkdown(form), [form]);

  function update<K extends keyof Form>(key: K, value: Form[K]) {
    setForm((curr) => ({ ...curr, [key]: value }));
  }

  function loadExample() {
    setForm(EXAMPLE);
  }

  function clear() {
    if (!confirm('Clear all fields?')) return;
    setForm(BLANK);
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(markdown);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  }

  function downloadFile() {
    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const slug = (form.featureName || 'prd').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    a.download = `${slug || 'prd'}.md`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (!hydrated) {
    return (
      <div className="bg-white dark:bg-card border border-border/60 rounded-2xl p-12 text-center text-muted-foreground">
        Loading generator…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white dark:bg-card border border-border/60 rounded-2xl p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-1">Feature name</label>
            <input
              type="text"
              value={form.featureName}
              onChange={(e) => update('featureName', e.target.value)}
              placeholder="e.g. Inline comments on roadmap items"
              className="w-full bg-transparent border border-border/60 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1">Author</label>
              <input
                type="text"
                value={form.author}
                onChange={(e) => update('author', e.target.value)}
                placeholder="Your name"
                className="w-full bg-transparent border border-border/60 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1">Status</label>
              <select
                value={form.status}
                onChange={(e) => update('status', e.target.value as Status)}
                className="w-full bg-transparent border border-border/60 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option>Draft</option>
                <option>In Review</option>
                <option>Approved</option>
                <option>Shipped</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-1">
              One-line summary
            </label>
            <input
              type="text"
              value={form.summary}
              onChange={(e) => update('summary', e.target.value)}
              placeholder="A single sentence anyone could read in 5 seconds"
              className="w-full bg-transparent border border-border/60 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <FieldArea
            label="Problem"
            hint="What is broken today, in user terms"
            value={form.problem}
            onChange={(v) => update('problem', v)}
          />
          <FieldArea
            label="Target users"
            hint="Who specifically will benefit"
            value={form.targetUsers}
            onChange={(v) => update('targetUsers', v)}
          />
          <FieldArea
            label="Goals"
            hint="One per line"
            value={form.goals}
            onChange={(v) => update('goals', v)}
          />
          <FieldArea
            label="Non-goals"
            hint="One per line — what we are explicitly NOT doing"
            value={form.nonGoals}
            onChange={(v) => update('nonGoals', v)}
          />
          <FieldArea
            label="Proposed solution"
            hint="The shape of the solution, not the full design"
            value={form.solution}
            onChange={(v) => update('solution', v)}
          />
          <FieldArea
            label="User stories"
            hint='One per line — "As a X, I want Y so Z"'
            value={form.userStories}
            onChange={(v) => update('userStories', v)}
          />
          <FieldArea
            label="Success metrics"
            hint="One per line — how we know this worked"
            value={form.metrics}
            onChange={(v) => update('metrics', v)}
          />
          <FieldArea
            label="Open questions"
            hint="One per line"
            value={form.openQuestions}
            onChange={(v) => update('openQuestions', v)}
          />
          <FieldArea
            label="Risks & dependencies"
            hint="One per line"
            value={form.risks}
            onChange={(v) => update('risks', v)}
          />
        </div>

        <div className="lg:sticky lg:top-6 lg:self-start space-y-3">
          <div className="bg-white dark:bg-card border border-border/60 rounded-2xl overflow-hidden">
            <div className="border-b border-border/60 bg-muted/30 px-4 py-2 text-xs font-semibold text-muted-foreground flex items-center justify-between">
              <span>Markdown preview</span>
              <span className="font-mono">.md</span>
            </div>
            <pre className="px-4 py-4 text-xs whitespace-pre-wrap font-mono leading-relaxed max-h-[70vh] overflow-y-auto">
              {markdown}
            </pre>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={copy}
              className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold shadow-sm hover:-translate-y-0.5 hover:shadow-md active:translate-y-0 transition-all"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied' : 'Copy Markdown'}
            </button>
            <button
              onClick={downloadFile}
              className="inline-flex items-center gap-2 rounded-lg border border-border/60 px-4 py-2 text-sm font-medium hover:bg-muted/50 transition-colors"
            >
              <Download className="w-4 h-4" />
              Download .md
            </button>
            <button
              onClick={loadExample}
              className="inline-flex items-center gap-2 rounded-lg border border-border/60 px-4 py-2 text-sm font-medium hover:bg-muted/50 transition-colors"
            >
              <FileText className="w-4 h-4" />
              Load example
            </button>
            <button
              onClick={clear}
              className="inline-flex items-center gap-2 rounded-lg border border-border/60 px-4 py-2 text-sm font-medium hover:bg-muted/50 transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              Clear
            </button>
          </div>
        </div>
      </div>
      <p className="text-xs text-muted-foreground text-center pt-2">
        Your draft is saved automatically in your browser. Nothing leaves this page.
      </p>
    </div>
  );
}

function FieldArea({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="block text-xs font-semibold text-muted-foreground mb-1">
        {label} <span className="font-normal text-muted-foreground/70">— {hint}</span>
      </label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className="w-full bg-transparent border border-border/60 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary resize-y"
      />
    </div>
  );
}
